/**
 * Provides privacy-safe, authenticated access to the LawbaseAPI Archived
 * Emails proxy. The module normalizes the shared response envelope without
 * logging or persisting email metadata, bodies, filters, or request details.
 */
import axios from "../http-config/axiosConfig";

const ARCHIVED_EMAILS_ROUTE = "/ArchivedEmails";
const AUTHORIZATION_STATUS_CODES = new Set([401, 403]);

export const ARCHIVED_EMAIL_ERROR_CODES = Object.freeze({
	LIST_FAILED: "ARCHIVED_EMAIL_LIST_FAILED",
	DETAIL_FAILED: "ARCHIVED_EMAIL_DETAIL_FAILED",
	NOT_FOUND: "ARCHIVED_EMAIL_NOT_FOUND",
});

export const ARCHIVED_EMAIL_ERROR_MESSAGES = Object.freeze({
	list: "Archived emails could not be loaded.",
	detail: "The email body could not be loaded.",
	notFound: "The archived email is no longer available.",
});

/**
 * Creates a client-owned error whose contents are safe to display.
 *
 * @param {string} code Stable error category used by the detail UI.
 * @param {string} message Fixed user-facing message.
 * @returns {Error} Sanitized error without a raw response or request object.
 */
const createSanitizedError = (code, message) => {
	const error = new Error(message);
	error.name = "ArchivedEmailError";
	error.code = code;
	error.isArchivedEmailSafeError = true;
	return error;
};

/**
 * Identifies responses that must remain available to the application's
 * authentication and authorization handling.
 *
 * @param {*} error Potential Axios error.
 * @returns {boolean} Whether the HTTP status is 401 or 403.
 */
export const isArchivedEmailAuthorizationError = (error) =>
	AUTHORIZATION_STATUS_CODES.has(error?.response?.status);

/**
 * Identifies cancellation caused by collapsing or unmounting a detail view.
 * Cancellation is control flow and must not be replaced with a failure message.
 *
 * @param {*} error Potential Axios or browser cancellation error.
 * @returns {boolean} Whether the request was canceled.
 */
export const isArchivedEmailCancellationError = (error) =>
	error?.code === "ERR_CANCELED" ||
	error?.name === "AbortError" ||
	error?.name === "CanceledError";

/**
 * Serializes allowlisted query values and preserves repeated array keys.
 * Axios' default bracket-style array syntax is deliberately avoided because
 * the ASP.NET contract expects repeated keys such as senderValues=a&senderValues=b.
 *
 * @param {object} parameters Allowlisted query parameter object.
 * @returns {string} URL-encoded query string.
 */
export const serializeArchivedEmailQuery = (parameters = {}) => {
	const query = new URLSearchParams();

	Object.entries(parameters).forEach(([key, value]) => {
		const appendValue = (candidate) => {
			if (
				candidate === undefined ||
				candidate === null ||
				candidate === ""
			) {
				return;
			}

			query.append(
				key,
				candidate instanceof Date
					? candidate.toISOString()
					: String(candidate)
			);
		};

		if (Array.isArray(value)) {
			value.forEach(appendValue);
			return;
		}

		appendValue(value);
	});

	return query.toString();
};

/**
 * Reads a successful shared response envelope or produces a fixed client
 * failure instead of exposing a downstream message.
 *
 * @param {object} data LawbaseAPI response envelope.
 * @param {string} code Stable client error category.
 * @param {string} message Fixed user-facing failure message.
 * @returns {*} Successful response result.
 * @throws {Error} Sanitized business failure.
 */
const unwrapResponse = (data, code, message) => {
	if (!data?.isSuccess) {
		throw createSanitizedError(code, message);
	}

	return data.result;
};

/**
 * Converts a failed request into the approved client error contract while
 * preserving authentication statuses for the existing authorization flow.
 *
 * @param {*} error Axios or client normalization error.
 * @param {string} code Stable fallback error category.
 * @param {string} message Fixed fallback message.
 * @param {boolean} recognizeNotFound Whether a 404 is a missing detail.
 * @throws {Error} Original authorization error or a sanitized client error.
 */
const throwSanitizedRequestError = (
	error,
	code,
	message,
	recognizeNotFound = false
) => {
	if (
		isArchivedEmailAuthorizationError(error) ||
		isArchivedEmailCancellationError(error) ||
		error?.isArchivedEmailSafeError
	) {
		throw error;
	}

	if (recognizeNotFound && error?.response?.status === 404) {
		throw createSanitizedError(
			ARCHIVED_EMAIL_ERROR_CODES.NOT_FOUND,
			ARCHIVED_EMAIL_ERROR_MESSAGES.notFound
		);
	}

	throw createSanitizedError(code, message);
};

/**
 * Normalizes one metadata row so nullable strings never leak into text cells.
 *
 * @param {object} item Archived-email summary returned by LawbaseAPI.
 * @returns {object} Stable grid row.
 */
const normalizeSummary = (item = {}) => ({
	id: item.id,
	caseNumber: item.caseNumber ?? "",
	sender: item.sender ?? "",
	recipient: item.recipient ?? "",
	subject: item.subject ?? "",
	sentDateTime: item.sentDateTime ?? null,
});

/**
 * Normalizes a paged result for DevExtreme.
 *
 * @param {object} result Paged result from the response envelope.
 * @param {Function} normalizeItem Item normalization function.
 * @returns {{items: Array, totalCount: number}} Stable page.
 */
const normalizePage = (result, normalizeItem) => {
	const items = Array.isArray(result?.items)
		? result.items.map(normalizeItem)
		: [];
	const totalCount =
		Number.isInteger(result?.totalCount) && result.totalCount >= 0
			? result.totalCount
			: 0;

	return { items, totalCount };
};

/**
 * Loads one bounded page of archived-email summaries.
 *
 * @param {object} query Allowlisted paging, sorting, and filter parameters.
 * @returns {Promise<{items: Array, totalCount: number}>} Normalized page.
 */
export const getArchivedEmailsAsync = async (query = {}) => {
	try {
		const { data } = await axios.get(ARCHIVED_EMAILS_ROUTE, {
			params: query,
			paramsSerializer: {
				serialize: serializeArchivedEmailQuery,
			},
		});
		const result = unwrapResponse(
			data,
			ARCHIVED_EMAIL_ERROR_CODES.LIST_FAILED,
			ARCHIVED_EMAIL_ERROR_MESSAGES.list
		);
		return normalizePage(result, normalizeSummary);
	} catch (error) {
		throwSanitizedRequestError(
			error,
			ARCHIVED_EMAIL_ERROR_CODES.LIST_FAILED,
			ARCHIVED_EMAIL_ERROR_MESSAGES.list
		);
	}
};

/**
 * Loads bounded distinct values for one approved grid header filter.
 *
 * @param {object} query Field, search, skip, and take parameters.
 * @returns {Promise<{items: Array, totalCount: number}>} Normalized values.
 */
export const getArchivedEmailFilterValuesAsync = async (query = {}) => {
	try {
		const { data } = await axios.get(
			`${ARCHIVED_EMAILS_ROUTE}/filter-values`,
			{
				params: query,
				paramsSerializer: {
					serialize: serializeArchivedEmailQuery,
				},
			}
		);
		const result = unwrapResponse(
			data,
			ARCHIVED_EMAIL_ERROR_CODES.LIST_FAILED,
			ARCHIVED_EMAIL_ERROR_MESSAGES.list
		);
		return normalizePage(result, (item = {}) => ({
			value: item.value ?? "",
			text: item.text ?? "",
		}));
	} catch (error) {
		throwSanitizedRequestError(
			error,
			ARCHIVED_EMAIL_ERROR_CODES.LIST_FAILED,
			ARCHIVED_EMAIL_ERROR_MESSAGES.list
		);
	}
};

/**
 * Loads the inert plain-text body for one archived email.
 *
 * @param {number} id Positive archived-email identifier.
 * @param {{signal?: AbortSignal}} options Optional cancellation signal.
 * @returns {Promise<{id: number, plainTextBody: string}>} Normalized detail.
 */
export const getArchivedEmailByIdAsync = async (
	id,
	{ signal } = {}
) => {
	const normalizedId = Number(id);
	if (!Number.isInteger(normalizedId) || normalizedId <= 0) {
		throw createSanitizedError(
			ARCHIVED_EMAIL_ERROR_CODES.DETAIL_FAILED,
			ARCHIVED_EMAIL_ERROR_MESSAGES.detail
		);
	}

	try {
		const { data } = await axios.get(
			`${ARCHIVED_EMAILS_ROUTE}/${normalizedId}`,
			{ signal }
		);
		const result = unwrapResponse(
			data,
			ARCHIVED_EMAIL_ERROR_CODES.DETAIL_FAILED,
			ARCHIVED_EMAIL_ERROR_MESSAGES.detail
		);
		return {
			id: result?.id ?? normalizedId,
			plainTextBody: result?.plainTextBody ?? "",
		};
	} catch (error) {
		throwSanitizedRequestError(
			error,
			ARCHIVED_EMAIL_ERROR_CODES.DETAIL_FAILED,
			ARCHIVED_EMAIL_ERROR_MESSAGES.detail,
			true
		);
	}
};

const archivedEmailsApi = {
	getArchivedEmailsAsync,
	getArchivedEmailFilterValuesAsync,
	getArchivedEmailByIdAsync,
};

export default archivedEmailsApi;
